import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import { useEffect, lazy, Suspense } from "react";
import { Helmet, HelmetProvider } from "react-helmet-async";
import Header from "./components/Header";
import Footer from "./components/Footer";
import WhatsAppButton from "./components/WhatsAppButton";
import CookieBanner from "./components/CookieBanner";
import BudgetPopup from "./components/BudgetPopup";
import Index from "./pages/Index";

const Empresa = lazy(() => import("./pages/Empresa"));
const Solucoes = lazy(() => import("./pages/Solucoes"));
const Atuacao = lazy(() => import("./pages/Atuacao"));
const Aplicativo = lazy(() => import("./pages/Aplicativo"));
const FAQ = lazy(() => import("./pages/FAQ"));
const Contato = lazy(() => import("./pages/Contato"));
const InformativoApp = lazy(() => import("./pages/InformativoApp"));
const NotFound = lazy(() => import("./pages/NotFound"));

const ScrollToTop = () => {
  const { pathname } = useLocation();
  useEffect(() => { window.scrollTo(0, 0); }, [pathname]);
  return null;
};

const CanonicalRedirect = () => {
  useEffect(() => {
    if (typeof window === "undefined") return;
    try { if (window.self !== window.top) return; } catch { return; }
    const host = window.location.hostname;
    if (host === "acesso.srv.br") return;
    const shouldRedirect = host === "www.acesso.srv.br" || host.endsWith(".lovable.app");
    if (!shouldRedirect) return;
    const url = `https://acesso.srv.br${window.location.pathname}${window.location.search}${window.location.hash}`;
    window.location.replace(url);
  }, []);
  return null;
};

const queryClient = new QueryClient();

const App = () => (
  <HelmetProvider>
    <QueryClientProvider client={queryClient}>
      <TooltipProvider>
        <Toaster />
        <Sonner />
        <BrowserRouter>
          <Helmet>
            <title>Portaria Remota em Goiânia | Acesso Sistemas de Segurança</title>
            <meta name="description" content="Portaria remota em Goiânia com monitoramento 24h, controle de acesso inteligente e tecnologia para condomínios e empresas." />
            <meta name="keywords" content="portaria remota goiânia, segurança condominial, monitoramento 24h, controle de acesso inteligente, portaria inteligente, empresa de segurança goiânia, portaria remota goiás, segurança eletrônica" />
            <meta property="og:title" content="Portaria Remota em Goiânia | Acesso Sistemas de Segurança" />
            <meta property="og:description" content="Portaria remota em Goiânia com monitoramento 24h, controle de acesso inteligente e tecnologia para condomínios e empresas." />
            <meta property="og:type" content="website" />
            <meta property="og:url" content="https://acesso.srv.br/" />
            <meta property="og:image" content="https://acesso.srv.br/og-image.png" />
            <meta property="og:locale" content="pt_BR" />
            <meta name="twitter:card" content="summary_large_image" />
            <meta name="twitter:title" content="Portaria Remota em Goiânia | Acesso Sistemas de Segurança" />
            <meta name="twitter:description" content="Portaria remota em Goiânia com monitoramento 24h, controle de acesso inteligente e tecnologia para condomínios e empresas." />
            <meta name="twitter:image" content="https://acesso.srv.br/og-image.png" />
            <script type="application/ld+json">{JSON.stringify({
              "@context": "https://schema.org",
              "@type": "LocalBusiness",
              "name": "Acesso Sistemas de Segurança",
              "description": "Portaria remota e segurança eletrônica para condomínios em Goiânia e região",
              "image": "https://acesso.srv.br/og-image.png",
              "address": { "@type": "PostalAddress", "addressLocality": "Goiânia", "addressRegion": "GO", "addressCountry": "BR", "streetAddress": "Av. T-3, 2115 - Qd. 93 Lt. 18 - St. Bueno", "postalCode": "74210-245" },
              "geo": { "@type": "GeoCoordinates", "latitude": -16.7045, "longitude": -49.2772 },
              "telephone": "08009402501",
              "url": "https://acesso.srv.br",
              "openingHoursSpecification": { "@type": "OpeningHoursSpecification", "dayOfWeek": ["Monday","Tuesday","Wednesday","Thursday","Friday","Saturday","Sunday"], "opens": "00:00", "closes": "23:59" },
              "areaServed": [
                { "@type": "State", "name": "Goiás" },
                { "@type": "State", "name": "Distrito Federal" },
                { "@type": "State", "name": "Minas Gerais" },
                { "@type": "State", "name": "São Paulo" },
                { "@type": "State", "name": "Mato Grosso" }
              ],
              "sameAs": [
                "https://www.facebook.com/acessogo/",
                "https://www.instagram.com/acesso.srv/",
                "https://br.linkedin.com/company/acesso-sistemas-de-seguran%C3%A7a"
              ]
            })}</script>
          </Helmet>
          <CanonicalRedirect />
          <ScrollToTop />
          <Header />
          <Suspense fallback={null}>
            <Routes>
              <Route path="/" element={<Index />} />
              <Route path="/empresa" element={<Empresa />} />
              <Route path="/solucoes" element={<Solucoes />} />
              <Route path="/atuacao" element={<Atuacao />} />
              <Route path="/aplicativo" element={<Aplicativo />} />
              <Route path="/faq" element={<FAQ />} />
              <Route path="/contato" element={<Contato />} />
              <Route path="/informativo-app" element={<InformativoApp />} />
              <Route path="*" element={<NotFound />} />
            </Routes>
          </Suspense>
          <Footer />
          <WhatsAppButton />
          <CookieBanner />
          <BudgetPopup />
        </BrowserRouter>
      </TooltipProvider>
    </QueryClientProvider>
  </HelmetProvider>
);

export default App;
